import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../api";
import { fmt } from "../endomarketing-utils";
import type { Row } from "../types";
import {
  IconClose,
  IconPlus,
  IconTrash,
  IconSparkles,
  IconCheck,
  IconStar,
  IconDownload,
} from "../icons";
import { usePodeExcluir } from "../permissoes";

/**
 * PLANO DE AÇÃO de um colaborador — montar, acompanhar e fechar.
 *
 * O plano inteiro é editado em memória e salvo de uma vez (cabeçalho + ações),
 * como o formulário de qualquer cadastro do sistema: quem monta um plano
 * escreve as ações de uma sentada, e salvar ação por ação deixaria plano pela
 * metade se a pessoa fechasse a tela no meio.
 *
 * Ao salvar, a tela compara com o que veio do servidor: ação nova entra, ação
 * mexida é atualizada e ação removida da lista é apagada.
 */

const TIPOS = [
  "Treinamento",
  "Acompanhamento",
  "Técnico",
  "Segurança",
  "Comportamental",
  "Metas",
  "Outro",
];
const STATUS = ["Pendente", "Em andamento", "Concluída", "Cancelada"];
const SITUACOES = ["Rascunho", "Em andamento", "Concluído", "Cancelado"];

interface ItemPlano {
  /** id do servidor; ausente = ação nova, ainda não gravada. */
  id?: number;
  acao: string;
  tipo: string;
  como: string;
  responsavel: string;
  prazo: string;
  status: string;
  observacao: string;
  concluido_em?: string | null;
  atrasada?: boolean;
}

const ITEM_VAZIO: ItemPlano = {
  acao: "",
  tipo: "Acompanhamento",
  como: "",
  responsavel: "",
  prazo: "",
  status: "Pendente",
  observacao: "",
};

/** Data de hoje + N dias, em AAAA-MM-DD (prazo sugerido pela IA). */
function emDias(dias: number): string {
  const d = new Date();
  d.setDate(d.getDate() + dias);
  return d.toISOString().slice(0, 10);
}

export default function PlanoAcaoModal({
  planoId,
  colaborador,
  onClose,
  onSalvo,
}: {
  planoId: number | null;
  colaborador: Row;
  onClose: () => void;
  onSalvo: (mensagem: string) => void | Promise<void>;
}) {
  const novo = planoId == null;
  const podeExcluir = usePodeExcluir(); // só admin apaga registro (regra do Portal)

  const [titulo, setTitulo] = useState(`Plano de ação — ${colaborador.nome}`);
  const [periodo, setPeriodo] = useState(String(new Date().getFullYear()));
  const [objetivo, setObjetivo] = useState("");
  const [fortes, setFortes] = useState("");
  const [melhoria, setMelhoria] = useState("");
  const [situacao, setSituacao] = useState("Em andamento");
  const [prazo, setPrazo] = useState("");
  const [nota, setNota] = useState(
    colaborador.ultima_nota == null ? "" : String(colaborador.ultima_nota),
  );

  const [itens, setItens] = useState<ItemPlano[]>([]);
  const [originais, setOriginais] = useState<ItemPlano[]>([]);
  const [avaliacoes, setAvaliacoes] = useState<Row[]>([]);
  const [treinamentos, setTreinamentos] = useState<Row[]>([]);

  const [carregando, setCarregando] = useState(!novo);
  const [salvando, setSalvando] = useState(false);
  const [pensandoIa, setPensandoIa] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [avisoIa, setAvisoIa] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    if (planoId == null) return;
    setCarregando(true);
    try {
      const r = await api.desempenho.plano(planoId);
      const p = r.plano;
      setTitulo(String(p.titulo ?? ""));
      setPeriodo(String(p.periodo ?? ""));
      setObjetivo(String(p.objetivo ?? ""));
      setFortes(String(p.pontos_fortes ?? ""));
      setMelhoria(String(p.pontos_melhoria ?? ""));
      setSituacao(String(p.situacao ?? "Em andamento"));
      setPrazo(String(p.prazo ?? "").slice(0, 10));
      setNota(p.nota_inicial == null ? "" : String(p.nota_inicial));
      const lista: ItemPlano[] = (r.itens ?? []).map((i: Row) => ({
        id: Number(i.id),
        acao: String(i.acao ?? ""),
        tipo: String(i.tipo ?? "Acompanhamento"),
        como: String(i.como ?? ""),
        responsavel: String(i.responsavel ?? ""),
        prazo: String(i.prazo ?? "").slice(0, 10),
        status: String(i.status ?? "Pendente"),
        observacao: String(i.observacao ?? ""),
        concluido_em: i.concluido_em ?? null,
        atrasada: !!i.atrasada,
      }));
      setItens(lista);
      setOriginais(lista);
      setAvaliacoes(r.avaliacoes ?? []);
      setTreinamentos((r as { treinamentos?: Row[] }).treinamentos ?? []);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Erro ao carregar o plano.");
    } finally {
      setCarregando(false);
    }
  }, [planoId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const validas = itens.filter((i) => i.acao.trim() && i.status !== "Cancelada");
  const feitas = validas.filter((i) => i.status === "Concluída").length;
  const pct = validas.length ? Math.round((feitas / validas.length) * 100) : 0;
  const atrasadas = useMemo(
    () =>
      itens.filter(
        (i) =>
          i.prazo &&
          i.status !== "Concluída" &&
          i.status !== "Cancelada" &&
          i.prazo < new Date().toISOString().slice(0, 10),
      ).length,
    [itens],
  );

  function mudarItem(indice: number, campo: keyof ItemPlano, valor: string) {
    setItens((prev) =>
      prev.map((it, i) => (i === indice ? { ...it, [campo]: valor } : it)),
    );
  }

  async function sugerirComIa() {
    setPensandoIa(true);
    setErro(null);
    setAvisoIa(null);
    try {
      const s = await api.desempenho.sugerirAcoes(Number(colaborador.id), melhoria || objetivo);
      // A IA SUGERE: acrescenta às ações que já existem (não apaga o que foi
      // escrito à mão) e só preenche os campos de texto que estiverem vazios.
      if (!objetivo.trim() && s.objetivo) setObjetivo(s.objetivo);
      if (!fortes.trim() && s.pontos_fortes) setFortes(s.pontos_fortes);
      if (!melhoria.trim() && s.pontos_melhoria) setMelhoria(s.pontos_melhoria);

      const jaTem = new Set(itens.map((i) => i.acao.trim().toLowerCase()));
      const novas: ItemPlano[] = (s.acoes ?? [])
        .filter((a: any) => a?.acao && !jaTem.has(String(a.acao).trim().toLowerCase()))
        .map((a: any) => ({
          ...ITEM_VAZIO,
          acao: String(a.acao),
          tipo: TIPOS.includes(String(a.tipo)) ? String(a.tipo) : "Acompanhamento",
          como: String(a.como ?? ""),
          responsavel: String(a.responsavel ?? ""),
          prazo: emDias(Number(a.prazo_dias) || 30),
        }));
      setItens((prev) => [...prev, ...novas]);
      setAvisoIa(
        novas.length === 0
          ? "A sugestão não trouxe ação nova (as propostas já estavam na lista)."
          : s.ia
            ? `${novas.length} ação(ões) sugerida(s) pela IA — revise, ajuste e salve.`
            : `${novas.length} ação(ões) do roteiro padrão (IA não configurada) — revise e ajuste.`,
      );
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível sugerir as ações.");
    } finally {
      setPensandoIa(false);
    }
  }

  async function salvar() {
    setErro(null);
    if (!titulo.trim()) return setErro("Dê um título ao plano.");
    const comAcao = itens.filter((i) => i.acao.trim());
    if (comAcao.length === 0) {
      return setErro("Um plano de ação precisa de pelo menos uma ação. Use '+ Adicionar ação'.");
    }

    setSalvando(true);
    try {
      const cabecalho = {
        colaborador_id: colaborador.id,
        titulo: titulo.trim(),
        periodo,
        objetivo,
        pontos_fortes: fortes,
        pontos_melhoria: melhoria,
        situacao,
        prazo,
        nota_inicial: nota,
      };

      if (novo) {
        await api.desempenho.criarPlano({ ...cabecalho, itens: comAcao });
        await onSalvo(`Plano de ação de ${colaborador.nome} criado com ${comAcao.length} ação(ões).`);
        return;
      }

      await api.desempenho.atualizarPlano(planoId!, cabecalho);

      // Diferença contra o que veio do servidor: novas, alteradas e removidas.
      const idsAgora = new Set(comAcao.filter((i) => i.id).map((i) => i.id));
      for (const antigo of originais) {
        if (antigo.id && !idsAgora.has(antigo.id)) {
          await api.desempenho.removerItem(antigo.id);
        }
      }
      for (const item of comAcao) {
        if (!item.id) {
          await api.desempenho.criarItem(planoId!, { ...item });
          continue;
        }
        const antes = originais.find((o) => o.id === item.id);
        const mudou =
          !antes ||
          (["acao", "tipo", "como", "responsavel", "prazo", "status", "observacao"] as const).some(
            (c) => (antes[c] ?? "") !== (item[c] ?? ""),
          );
        if (mudou) await api.desempenho.atualizarItem(item.id, { ...item });
      }
      await onSalvo(`Plano de ação de ${colaborador.nome} atualizado.`);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Erro ao salvar o plano.");
      setSalvando(false);
    }
  }

  async function excluir() {
    if (planoId == null) return;
    if (!confirm(`Excluir o plano de ação de ${colaborador.nome} e todas as ações?`)) return;
    await api.desempenho.removerPlano(planoId);
    await onSalvo(`Plano de ação de ${colaborador.nome} excluído.`);
  }

  /** Versão para papel: o plano costuma ser assinado pelo colaborador e pelo gestor. */
  function imprimir() {
    const linhas = itens
      .filter((i) => i.acao.trim())
      .map(
        (i, n) => `<tr>
          <td>${n + 1}</td>
          <td><strong>${escapar(i.acao)}</strong>${i.como ? `<br><span class="peq">${escapar(i.como)}</span>` : ""}</td>
          <td>${escapar(i.tipo)}</td>
          <td>${escapar(i.responsavel)}</td>
          <td>${i.prazo ? fmt(i.prazo) : "—"}</td>
          <td>${escapar(i.status)}</td>
        </tr>`,
      )
      .join("");

    const html = `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8">
      <title>Plano de ação — ${escapar(String(colaborador.nome))}</title>
      <style>
        *{box-sizing:border-box} body{font-family:system-ui,Segoe UI,sans-serif;color:#13241b;margin:28px}
        h1{font-size:19px;margin:0 0 4px} h2{font-size:13px;margin:18px 0 6px;color:#0c7a3f}
        .cab{border-bottom:2px solid #0c7a3f;padding-bottom:10px;margin-bottom:14px}
        .linha{font-size:12.5px;color:#444;margin:2px 0}
        table{width:100%;border-collapse:collapse;font-size:12px;margin-top:6px}
        th,td{border:1px solid #cbd8d0;padding:6px 8px;text-align:left;vertical-align:top}
        th{background:#eaf5ee;font-size:11px;text-transform:uppercase;letter-spacing:.04em}
        .peq{font-size:11px;color:#5e7268}
        .txt{font-size:12.5px;line-height:1.5;white-space:pre-wrap;margin:0 0 8px}
        .ass{display:flex;gap:40px;margin-top:56px}
        .ass div{flex:1;border-top:1px solid #333;padding-top:6px;font-size:11.5px;text-align:center}
        @media print{body{margin:12mm}}
      </style></head><body>
      <div class="cab">
        <h1>Plano de ação — Avaliação de desempenho</h1>
        <p class="linha"><strong>${escapar(String(colaborador.nome))}</strong> · ${escapar(String(colaborador.cargo ?? "—"))} · ${escapar(String(colaborador.departamento ?? "—"))}</p>
        <p class="linha">Período: ${escapar(periodo || "—")} · Prazo do plano: ${prazo ? fmt(prazo) : "—"} · Situação: ${escapar(situacao)}${nota ? ` · Nota de partida: ${escapar(nota)}` : ""}</p>
      </div>
      ${objetivo ? `<h2>Objetivo</h2><p class="txt">${escapar(objetivo)}</p>` : ""}
      ${fortes ? `<h2>Pontos fortes</h2><p class="txt">${escapar(fortes)}</p>` : ""}
      ${melhoria ? `<h2>Pontos a desenvolver</h2><p class="txt">${escapar(melhoria)}</p>` : ""}
      <h2>Ações</h2>
      <table><thead><tr><th>#</th><th>O que será feito / como</th><th>Tipo</th><th>Responsável</th><th>Prazo</th><th>Status</th></tr></thead>
      <tbody>${linhas}</tbody></table>
      <div class="ass"><div>Colaborador</div><div>Gestor da área</div><div>RH</div></div>
      </body></html>`;

    const janela = window.open("", "_blank");
    if (!janela) {
      setErro("O navegador bloqueou a janela de impressão. Libere os pop-ups deste site.");
      return;
    }
    janela.document.write(html);
    janela.document.close();
    janela.focus();
    janela.print();
  }

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card plano-card" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h3>{novo ? "Montar plano de ação" : "Plano de ação"}</h3>
            <p className="modal-sub">
              {colaborador.nome} · {colaborador.cargo || colaborador.departamento || "—"}
              {!novo && ` · ${feitas} de ${validas.length} ação(ões) concluída(s)`}
            </p>
          </div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body">
          {carregando ? (
            <div className="empty">Carregando o plano…</div>
          ) : (
            <>
              <div className="form-grid plano-cabecalho">
                <label className="field field-wide">
                  <span>
                    Título <em className="req">*</em>
                  </span>
                  <input value={titulo} onChange={(e) => setTitulo(e.target.value)} />
                </label>
                <label className="field">
                  <span>Período</span>
                  <input
                    value={periodo}
                    onChange={(e) => setPeriodo(e.target.value)}
                    placeholder="Ex.: 2026 ou 1º semestre/2026"
                  />
                </label>
                <label className="field">
                  <span>Prazo do plano</span>
                  <input type="date" value={prazo} onChange={(e) => setPrazo(e.target.value)} />
                </label>
                <label className="field">
                  <span>Situação</span>
                  <select value={situacao} onChange={(e) => setSituacao(e.target.value)}>
                    {SITUACOES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span>Nota de partida</span>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="10"
                    value={nota}
                    onChange={(e) => setNota(e.target.value)}
                    placeholder="da última avaliação"
                  />
                </label>
                <label className="field field-wide">
                  <span>Objetivo do plano</span>
                  <textarea
                    rows={2}
                    value={objetivo}
                    onChange={(e) => setObjetivo(e.target.value)}
                    placeholder="O que se espera do colaborador ao fim do período."
                  />
                </label>
                <label className="field">
                  <span>Pontos fortes</span>
                  <textarea rows={2} value={fortes} onChange={(e) => setFortes(e.target.value)} />
                </label>
                <label className="field">
                  <span>Pontos a desenvolver</span>
                  <textarea rows={2} value={melhoria} onChange={(e) => setMelhoria(e.target.value)} />
                </label>
              </div>

              {avaliacoes.length > 0 && (
                <details className="plano-avaliacoes">
                  <summary>
                    <IconStar size={14} /> Avaliações deste colaborador ({avaliacoes.length})
                  </summary>
                  <ul>
                    {avaliacoes.map((a) => (
                      <li key={String(a.id)}>
                        <strong>{a.nota == null ? "—" : Number(a.nota).toFixed(1)}</strong>
                        <span>
                          {a.periodo || "período não informado"}
                          {a.avaliador ? ` · ${a.avaliador}` : ""}
                        </span>
                        {a.comentario && <small className="muted">{a.comentario}</small>}
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              {treinamentos.length > 0 && (
                <details className="plano-avaliacoes">
                  <summary>
                    <IconCheck size={14} /> Plano de ação / treinamentos deste colaborador (
                    {treinamentos.length})
                  </summary>
                  <ul>
                    {treinamentos.map((t) => (
                      <li key={String(t.id)}>
                        <strong>{t.status === "Concluído" ? "✓" : "•"}</strong>
                        <span>
                          {String(t.nome_treinamento ?? "—")}
                          {t.carga_horaria ? ` · ${t.carga_horaria}h` : ""}
                          {t.data ? ` · ${fmt(String(t.data))}` : ""}
                        </span>
                        <small className="muted">
                          {String(t.status ?? "—")}
                          {t.nota != null ? ` · nota ${t.nota}` : ""}
                        </small>
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              <div className="plano-acoes-head">
                <h4>
                  Ações do plano <span className="badge">{validas.length}</span>
                  {atrasadas > 0 && <span className="badge badge-alerta">{atrasadas} atrasada(s)</span>}
                </h4>
                <div className="plano-acoes-botoes">
                  <button
                    className="btn-secondary btn-sm"
                    onClick={sugerirComIa}
                    disabled={pensandoIa}
                    title="Sugerir ações a partir do cargo e das avaliações"
                  >
                    <IconSparkles size={15} /> {pensandoIa ? "Pensando…" : "Sugerir ações (IA)"}
                  </button>
                  <button
                    className="btn-primary btn-sm"
                    onClick={() => setItens((prev) => [...prev, { ...ITEM_VAZIO }])}
                  >
                    <IconPlus size={15} /> Adicionar ação
                  </button>
                </div>
              </div>

              {avisoIa && <div className="endo-aviso"><span>{avisoIa}</span></div>}

              {validas.length > 0 && (
                <div className="plano-progresso">
                  <div className="endo-barra">
                    <div className="endo-barra-fill" style={{ width: `${pct}%` }} />
                  </div>
                  <small className="muted">
                    {pct}% concluído · {feitas} de {validas.length} ação(ões)
                  </small>
                </div>
              )}

              {itens.length === 0 ? (
                <div className="empty">
                  Nenhuma ação ainda. Use <strong>Adicionar ação</strong> ou peça sugestões à IA.
                </div>
              ) : (
                <div className="plano-itens">
                  {itens.map((item, i) => (
                    <div
                      key={item.id ?? `novo-${i}`}
                      className={`plano-item ${item.status === "Concluída" ? "feita" : ""} ${
                        item.atrasada || (item.prazo && item.status !== "Concluída" && item.status !== "Cancelada" && item.prazo < new Date().toISOString().slice(0, 10))
                          ? "atrasada"
                          : ""
                      }`}
                    >
                      <div className="plano-item-topo">
                        <span className="plano-item-num">{i + 1}</span>
                        <input
                          className="plano-item-acao"
                          value={item.acao}
                          placeholder="O que será feito (comece com um verbo: fazer, concluir, acompanhar…)"
                          onChange={(e) => mudarItem(i, "acao", e.target.value)}
                        />
                        {/* Ação AINDA NÃO SALVA (sem id) qualquer um tira da
                            lista — é linha de formulário. Ação já gravada só
                            sai com o DELETE do servidor, que é de admin. */}
                        {(!item.id || podeExcluir) && (
                          <button
                            className="icon-btn icon-btn-danger"
                            title="Remover esta ação"
                            onClick={() => setItens((prev) => prev.filter((_, x) => x !== i))}
                          >
                            <IconTrash size={15} />
                          </button>
                        )}
                      </div>

                      <div className="plano-item-grade">
                        <label className="field">
                          <span>Tipo</span>
                          <select value={item.tipo} onChange={(e) => mudarItem(i, "tipo", e.target.value)}>
                            {TIPOS.map((t) => (
                              <option key={t} value={t}>
                                {t}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="field">
                          <span>Responsável</span>
                          <input
                            value={item.responsavel}
                            placeholder="Quem conduz"
                            onChange={(e) => mudarItem(i, "responsavel", e.target.value)}
                          />
                        </label>
                        <label className="field">
                          <span>Prazo</span>
                          <input
                            type="date"
                            value={item.prazo}
                            onChange={(e) => mudarItem(i, "prazo", e.target.value)}
                          />
                        </label>
                        <label className="field">
                          <span>Status</span>
                          <select
                            value={item.status}
                            onChange={(e) => mudarItem(i, "status", e.target.value)}
                          >
                            {STATUS.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="field field-wide">
                          <span>Como será feito</span>
                          <input
                            value={item.como}
                            placeholder="O caminho: treinamento, acompanhamento em campo, reunião…"
                            onChange={(e) => mudarItem(i, "como", e.target.value)}
                          />
                        </label>
                        <label className="field field-wide">
                          <span>Acompanhamento / evidência</span>
                          <input
                            value={item.observacao}
                            placeholder="O que avançou, o que travou, onde está a comprovação"
                            onChange={(e) => mudarItem(i, "observacao", e.target.value)}
                          />
                        </label>
                      </div>

                      {item.status === "Concluída" && item.concluido_em && (
                        <small className="plano-item-feita">
                          <IconCheck size={13} /> concluída em {fmt(item.concluido_em)}
                        </small>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-foot plano-foot">
          {erro && <div className="form-error">{erro}</div>}
          {!novo && (
            <>
              <button type="button" className="btn-secondary" onClick={imprimir}>
                <IconDownload size={16} /> Imprimir
              </button>
              {podeExcluir && (
                <button type="button" className="btn-secondary btn-danger-text" onClick={excluir}>
                  <IconTrash size={16} /> Excluir plano
                </button>
              )}
            </>
          )}
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="btn-primary" onClick={salvar} disabled={salvando || carregando}>
            {salvando ? "Salvando…" : novo ? "Criar plano" : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Escapa o texto que vai para a janela de impressão (é HTML montado à mão). */
function escapar(s: string): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
